import { Icon, IconNewTab } from '@flixbus/honeycomb-icons-react';
import { Link, List, Table, Text } from '@flixbus/honeycomb-react';
import { Fragment } from 'react';

import './htmlRenderer.css';

// Links are only rendered for these schemes, other links keep their text
const allowedLinkPattern = /^(https?:|mailto:)/i;

// Top-level inline content between these elements is wrapped in a paragraph
const blockElements = new Set(['p', 'pre', 'ul', 'ol', 'table']);

// Removed together with their content
const ignoredElements = new Set(['script', 'style']);

const renderLink = (element, children, key) => {
  const url = (element.getAttribute('href') || '').trim();
  if (!allowedLinkPattern.test(url)) {
    return <Fragment key={key}>{children}</Fragment>;
  }

  const target = url.toLowerCase().startsWith('mailto:') ? undefined : '_blank';
  return (
    <Link key={key} target={target} href={url}>
      {children} <Icon style={{ fill: 'currentColor' }} InlineIcon={IconNewTab} aria-label="Opens in a New Tab" />
    </Link>
  );
};

// Lists only contain items, whitespace between them is dropped
const withoutWhitespace = (children) => children.filter((child) => typeof child !== 'string' || child.trim());

// Honeycomb numbers list items with a CSS counter, so the start number is set on the counter
const renderNumberedList = (element, children, key) => {
  const start = Number(element.getAttribute('start')) || 1;
  const style = start > 1 ? { counterReset: `numbered-list-counter ${start - 1}` } : undefined;
  return (
    <List key={key} appearance="numbered" start={start > 1 ? start : undefined} style={style}>
      {withoutWhitespace(children)}
    </List>
  );
};

// Honeycomb tables take the header and body rows as arrays of cell content, so they are rendered from their rows
const renderTable = (element, key) => {
  const rows = Array.from(element.rows);
  const cells = (row, rowIndex) =>
    Array.from(row.cells).map((cell, index) => renderNodes(Array.from(cell.childNodes), `${key}-${rowIndex}-${index}`));
  const hasHeader = rows.length > 0 && Array.from(rows[0].cells).every((cell) => cell.nodeName === 'TH');
  const bodyRows = hasHeader ? rows.slice(1) : rows;

  return (
    <Table
      key={key}
      headers={hasHeader ? cells(rows[0], 0) : undefined}
      rows={bodyRows.map((row, index) => cells(row, index + 1))}
    />
  );
};

// Text colors set by the knowledge base in the data-color attribute
const textColors = new Set(['red', 'orange', 'green', 'blue', 'gray']);

const renderColoredText = (element, children, key) => {
  const color = element.getAttribute('data-color');
  if (!textColors.has(color)) {
    return <Fragment key={key}>{children}</Fragment>;
  }

  return (
    <span key={key} className={`rich-text-color-${color}`}>
      {children}
    </span>
  );
};

// Supported elements, any other element is replaced by its content
const elementRenderers = {
  a: renderLink,
  code: (element, children, key) => (
    <code key={key} className="rich-text-code">
      {children}
    </code>
  ),
  em: (element, children, key) => <em key={key}>{children}</em>,
  li: (element, children, key) => <li key={key}>{children}</li>,
  ol: renderNumberedList,
  p: (element, children, key) => (
    <Text key={key} Elem="p">
      {children}
    </Text>
  ),
  pre: (element, children, key) => (
    <pre key={key} className="rich-text-code-block">
      {children}
    </pre>
  ),
  ul: (element, children, key) => <List key={key}>{withoutWhitespace(children)}</List>,
  span: renderColoredText,
  sup: (element, children, key) => <sup key={key}>{children}</sup>,
  strong: (element, children, key) => (
    <Text key={key} Elem="strong" extraClasses="boldtext">
      {children}
    </Text>
  ),
};

// Rendered from their own DOM structure, their children are not rendered first
const structuredRenderers = {
  table: renderTable,
};

const renderNodes = (nodes, keyPrefix) => nodes.map((node, index) => renderNode(node, `${keyPrefix}-${index}`));

const renderNode = (node, key) => {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent;
  }

  const tagName = node.nodeName.toLowerCase();
  if (node.nodeType !== Node.ELEMENT_NODE || ignoredElements.has(tagName)) {
    return null;
  }

  const renderStructure = structuredRenderers[tagName];
  if (renderStructure) {
    return renderStructure(node, key);
  }

  const children = renderNodes(Array.from(node.childNodes), key);
  const render = elementRenderers[tagName];
  return render ? render(node, children, key) : <Fragment key={key}>{children}</Fragment>;
};

// Renders HTML from the knowledge base (news, FAQ) as React elements, the HTML is never injected
const htmlRenderer = (htmlText) => {
  const body = new DOMParser().parseFromString(htmlText || '', 'text/html').body;
  const content = [];
  let inlineNodes = [];

  const addParagraph = () => {
    if (inlineNodes.some((node) => node.textContent.trim())) {
      const key = `paragraph-${content.length}`;
      content.push(
        <Text key={key} Elem="p">
          {renderNodes(inlineNodes, key)}
        </Text>
      );
    }
    inlineNodes = [];
  };

  Array.from(body.childNodes).forEach((node) => {
    if (blockElements.has(node.nodeName.toLowerCase())) {
      addParagraph();
      content.push(renderNode(node, `block-${content.length}`));
    } else {
      inlineNodes.push(node);
    }
  });
  addParagraph();

  return content;
};

export default htmlRenderer;
